<template>
  <section style="min-height: 400px;">
    <nav class="level">
      <div class="level-left">
        <div class="level-item">
          <b-field>
            <p class="control">
              <span class="button is-static">
                {{ $t('attributes.activation_status') }}
              </span>
            </p>
            <b-select v-model="filterStatus" @input="onFilterStatus">
              <option value="">{{ $t('attributes.all') }}</option>
              <option value="true">{{ $t('attributes.shelve_on') }}</option>
              <option value="false">{{ $t('attributes.shelve_off') }}</option>
            </b-select>
          </b-field>
        </div>
      </div>

      <div class="level-right">
        <div class="level-item">
          <button class="button field is-primary" @click="onAdd">
            <b-icon icon="plus"></b-icon>
            <span>
              {{ $t('attributes.add') + $t('attributes.top_travel_group') }}
            </span>
          </button>
        </div>
      </div>
    </nav>

    <b-table
      :data="items"
      :loading="loading"
      hoverable
      narrowed
      bordered
      :default-sort-direction="defaultSortOrder"
      :default-sort="[sortField, sortOrder]"
    >
      <b-table-column
        field="sort"
        :label="$t('attributes.sort')"
        width="75"
        sortable
        numeric
        v-slot="props"
      >
        {{ props.row.sort }}
      </b-table-column>

      <b-table-column
        field="status"
        :label="$t('attributes.activation_status')"
        width="75"
        sortable
        centered
        v-slot="props"
      >
        <b-tag
          :type="props.row.status ? 'is-info' : 'is-danger'"
          size="is-medium"
        >
          {{ shelveStatus(props.row.status) }}
        </b-tag>
      </b-table-column>

      <b-table-column
        :label="$t('attributes.picture')"
        width="60"
        centered
        v-slot="props"
      >
        <a
          v-if="props.row.picture"
          :href="projectImageUrl(props.row.picture)"
          @click.prevent="onClickImage(props.row.picture)"
        >
          <figure class="image">
            <img
              :src="projectImageUrl(props.row.picture)"
              :alt="$t('attributes.picture')"
              style="width: 60px; margin: auto;"
            />
          </figure>
        </a>
      </b-table-column>

      <b-table-column
        field="name"
        :label="$t('attributes.name')"
        sortable
        v-slot="props"
      >
        <p>[{{ props.row.group_code }}]<span v-html="props.row.name"></span></p>
      </b-table-column>

      <b-table-column
        field="group_departure_date"
        :label="$t('attributes.departure_date')"
        width="120"
        centered
        sortable
        v-slot="props"
      >
        {{ formatDate(props.row.group_departure_date) }}
      </b-table-column>

      <b-table-column
        field="group_fee"
        :label="$t('attributes.fee')"
        width="120"
        numeric
        sortable
        v-slot="props"
      >
        {{ formatCurrency(props.row.group_fee) }}
      </b-table-column>

      <b-table-column
        custom-key="actions"
        :width="160"
        label="功能"
        centered
        v-slot="props"
      >
        <div class="buttons">
          <b-button type="is-primary" @click.stop.prevent="onModify(props.row)">
            {{ $t('attributes.modify') }}
          </b-button>
          <b-button
            type="is-danger"
            outlined
            @click.prevent="onDelete(props.row)"
          >
            {{ $t('attributes.delete') }}
          </b-button>
        </div>
      </b-table-column>

      <template #empty>
        <section class="section">
          <div v-if="!loading" class="content has-text-grey has-text-centered">
            <p>
              <b-icon icon="emoticon-sad" size="is-large" />
            </p>
          </div>
        </section>
      </template>
    </b-table>

    <b-modal
      :active.sync="isFormModalActive"
      :can-cancel="['escape', 'x']"
      :width="1200"
      has-modal-card
    >
      <top-travel-group-form :group="editItem" @send="onSend" />
    </b-modal>

    <b-modal :active.sync="isImageModalActive" :width="400">
      <p class="image is-square">
        <img :src="selectedImageUrl" width="40" alt="" />
      </p>
    </b-modal>
  </section>
</template>

<script>
import { TOP_TRAVEL_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'
import TopTravelGroupForm from './TopTravelGroupForm'

export default {
  name: 'TopTravelItem',
  components: {
    TopTravelGroupForm
  },

  props: {
    id: {
      required: true,
      type: String
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      items: [],

      filterStatus: '',
      sortField: 'sort',
      sortOrder: 'asc',
      defaultSortOrder: 'asc',

      isFormModalActive: false,
      editItem: null,

      isImageModalActive: false,
      selectedImageUrl: null
    }
  },
  watch: {
    id(newId) {
      this.loadItems(newId)
    }
  },
  mounted() {
    this.loadItems(this.id)
  },
  methods: {
    async loadItems(id) {
      try {
        const api = `${TOP_TRAVEL_API}/${id}/travel-groups`
        const params = {
          status: this.filterStatus && this.filterStatus === 'true',
          sort_by: this.sortField,
          by_descending: this.sortOrder !== 'asc'
        }

        this.$emit('update:loading', true)

        const res = await this.axios.get(api, { params })
        this.items = res.data.data
      } catch (e) {
        this.items = []

        notifyService.fetch_error('travel_group', e)
      } finally {
        this.$emit('update:loading', false)
      }
    },
    onFilterStatus() {
      this.loadItems(this.id)
    },
    onAdd() {
      this.editItem = {
        id: null,
        sort: 1,
        status: false,
        group_code: null,
        name: '',
        remark: '',
        picture: null
      }
      this.isFormModalActive = true
    },
    onModify(item) {
      this.editItem = item
      this.isFormModalActive = true
    },
    onDelete(item) {
      this.$buefy.dialog.confirm({
        message: this.$t('attributes.confirm_delete'),
        onConfirm: () => {
          this.deleteItem(item)
        }
      })
    },
    onSend(item) {
      if (item.id == null) {
        this.createItem(item)
      } else {
        this.updateItem(item)
      }
    },
    async createItem(item) {
      try {
        const api = `${TOP_TRAVEL_API}/${this.id}/travel-groups`

        this.$emit('update:loading', true)

        const res = await this.axios.post(api, item)
        const _item = res.data.data

        const items = [...this.items, _item]
        this.items = items.sort((t1, t2) => t1.sort - t2.sort)

        this.isFormModalActive = false
        notifyService.success(this.$t('attributes.add_succeed'))
      } catch (e) {
        notifyService.create_error(e)
      } finally {
        this.$emit('update:loading', false)
      }
    },
    async updateItem(item) {
      try {
        const api = `${TOP_TRAVEL_API}/${this.id}/travel-groups/${item.id}`

        this.$emit('update:loading', true)

        const res = await this.axios.put(api, item)
        const _item = res.data.data

        const idx = this.items.findIndex(s => s.id === _item.id)
        const items = [...this.items]
        items[idx] = _item
        this.items = items.sort((t1, t2) => t1.sort - t2.sort)

        notifyService.success(this.$t('attributes.update_succeed'))
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.$emit('update:loading', false)
      }
    },
    async deleteItem(item) {
      try {
        const api = `${TOP_TRAVEL_API}/${this.id}/travel-groups/${item.id}`

        this.$emit('update:loading', true)

        await this.axios.delete(api)
        this.items = this.items.filter(s => s.id !== item.id)

        notifyService.success(this.$t('attributes.delete_succeed'))
      } catch (e) {
        notifyService.delete_error(e)
      } finally {
        this.$emit('update:loading', false)
      }
    },
    onClickImage(url) {
      this.isImageModalActive = true
      this.selectedImageUrl = this.projectImageUrl(url)
    }
  }
}
</script>

<style lang="scss" scoped>
.b-table {
  td {
    vertical-align: middle;
  }
}
</style>
